import React from 'react';
import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    IconButton,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { MetadataFieldDef, MetadataFieldType } from '../AssetExplorer.types';
import { MetadataFieldInput } from './MetadataFieldInput';

export interface SchemaBuilderField extends MetadataFieldDef {
    clientId: string;
}

interface MetadataSchemaBuilderProps {
    fields: SchemaBuilderField[];
    onChange: (fields: SchemaBuilderField[]) => void;
    disabled?: boolean;
}

const FIELD_TYPES: Array<{ value: MetadataFieldType; label: string }> = [
    { value: 'TEXT', label: 'Text' },
    { value: 'TEXTAREA', label: 'Text Area' },
    { value: 'CHECKBOX', label: 'Checkbox group' },
    { value: 'DROPDOWN', label: 'Dropdown' },
    { value: 'TAGS', label: 'Tags' },
];

const needsOptions = (type: MetadataFieldType) => type === 'CHECKBOX' || type === 'DROPDOWN';

export const createEmptySchemaField = (order: number): SchemaBuilderField => ({
    clientId: `new-${Date.now()}-${order}`,
    fieldKey: '',
    label: '',
    type: 'TEXT',
    required: false,
    multiple: false,
    options: [],
    helpText: '',
    order,
    defaultValue: undefined,
});

export const toSchemaBuilderFields = (fields: MetadataFieldDef[]): SchemaBuilderField[] =>
    fields.map((field, index) => ({
        ...field,
        clientId: field.fieldKey || `field-${index}`,
        options: field.options || [],
        order: Number.isFinite(field.order) ? field.order : index,
    }));

export const MetadataSchemaBuilder: React.FC<MetadataSchemaBuilderProps> = ({
    fields,
    onChange,
    disabled = false,
}) => {
    const updateAt = (index: number, patch: Partial<SchemaBuilderField>) => {
        const next = fields.map((field, i) => (i === index ? { ...field, ...patch } : field));
        onChange(next.map((field, i) => ({ ...field, order: i })));
    };

    const move = (index: number, delta: number) => {
        const target = index + delta;
        if (target < 0 || target >= fields.length) return;
        const next = [...fields];
        const [row] = next.splice(index, 1);
        next.splice(target, 0, row);
        onChange(next.map((field, i) => ({ ...field, order: i })));
    };

    return (
        <Stack spacing={2}>
            {fields.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                    No fields yet. Add fields to define metadata for assets in this folder.
                </Typography>
            )}

            {fields.map((field, index) => (
                <Box
                    key={field.clientId}
                    sx={{
                        p: 2,
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 2,
                    }}
                >
                    <Stack spacing={1.5}>
                        <Stack direction="row" spacing={1} alignItems="flex-start">
                            <TextField
                                label="Label"
                                value={field.label}
                                onChange={(e) => updateAt(index, { label: e.target.value })}
                                fullWidth
                                disabled={disabled}
                                color="secondary"
                                size="small"
                            />
                            <TextField
                                select
                                label="Type"
                                value={field.type}
                                onChange={(e) => {
                                    const type = e.target.value as MetadataFieldType;
                                    updateAt(index, {
                                        type,
                                        multiple: type === 'DROPDOWN' ? field.multiple : false,
                                        options: needsOptions(type) ? field.options : [],
                                        defaultValue: undefined,
                                    });
                                }}
                                disabled={disabled}
                                color="secondary"
                                size="small"
                                sx={{ minWidth: 170 }}
                            >
                                {FIELD_TYPES.map((opt) => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <IconButton
                                size="small"
                                disabled={disabled || index === 0}
                                onClick={() => move(index, -1)}
                                aria-label="Move field up"
                            >
                                <ArrowUpwardIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                                size="small"
                                disabled={disabled || index === fields.length - 1}
                                onClick={() => move(index, 1)}
                                aria-label="Move field down"
                            >
                                <ArrowDownwardIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                                size="small"
                                color="error"
                                disabled={disabled}
                                onClick={() => onChange(fields.filter((_, i) => i !== index).map((f, i) => ({ ...f, order: i })))}
                                aria-label="Delete field"
                            >
                                <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                        </Stack>

                        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        color="secondary"
                                        checked={field.required}
                                        onChange={(e) => updateAt(index, { required: e.target.checked })}
                                        disabled={disabled}
                                    />
                                }
                                label="Required"
                            />
                            {field.type === 'DROPDOWN' && (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            color="secondary"
                                            checked={field.multiple}
                                            onChange={(e) =>
                                                updateAt(index, {
                                                    multiple: e.target.checked,
                                                    defaultValue: undefined,
                                                })
                                            }
                                            disabled={disabled}
                                        />
                                    }
                                    label="Allow multiple"
                                />
                            )}
                            {field.fieldKey && (
                                <Typography variant="caption" color="text.secondary">
                                    Key: {field.fieldKey}
                                </Typography>
                            )}
                        </Stack>

                        <TextField
                            label="Help text"
                            value={field.helpText || ''}
                            onChange={(e) => updateAt(index, { helpText: e.target.value })}
                            fullWidth
                            disabled={disabled}
                            color="secondary"
                            size="small"
                        />

                        {needsOptions(field.type) && (
                            <Stack spacing={1}>
                                <Typography variant="caption" color="text.secondary">
                                    Options
                                </Typography>
                                {field.options.map((opt, optIndex) => (
                                    <Stack key={`${field.clientId}-opt-${optIndex}`} direction="row" spacing={1}>
                                        <TextField
                                            label="Option label"
                                            value={opt.label}
                                            onChange={(e) => {
                                                const label = e.target.value;
                                                const options = field.options.map((o, i) =>
                                                    i === optIndex
                                                        ? { label, value: o.value || label.trim().toLowerCase().replace(/\s+/g, '_') }
                                                        : o,
                                                );
                                                updateAt(index, { options });
                                            }}
                                            size="small"
                                            fullWidth
                                            disabled={disabled}
                                            color="secondary"
                                        />
                                        <IconButton
                                            size="small"
                                            color="error"
                                            disabled={disabled}
                                            onClick={() =>
                                                updateAt(index, {
                                                    options: field.options.filter((_, i) => i !== optIndex),
                                                })
                                            }
                                            aria-label="Delete option"
                                        >
                                            <DeleteOutlineIcon fontSize="small" />
                                        </IconButton>
                                    </Stack>
                                ))}
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color="secondary"
                                    startIcon={<AddIcon />}
                                    disabled={disabled}
                                    onClick={() =>
                                        updateAt(index, {
                                            options: [...field.options, { value: `opt_${field.options.length + 1}`, label: '' }],
                                        })
                                    }
                                    sx={{ alignSelf: 'flex-start', borderWidth: 2, '&:hover': { borderWidth: 2 } }}
                                >
                                    Add option
                                </Button>
                            </Stack>
                        )}

                        <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                Default value
                            </Typography>
                            <MetadataFieldInput
                                field={{
                                    ...field,
                                    fieldKey: field.fieldKey || field.clientId,
                                    required: false,
                                    helpText: undefined,
                                    label: 'Default value',
                                }}
                                value={
                                    field.defaultValue !== undefined
                                        ? field.defaultValue
                                        : field.type === 'TAGS' ||
                                            field.type === 'CHECKBOX' ||
                                            (field.type === 'DROPDOWN' && field.multiple)
                                          ? []
                                          : ''
                                }
                                onChange={(_key, value) => updateAt(index, { defaultValue: value })}
                                disabled={disabled}
                            />
                        </Box>
                    </Stack>
                </Box>
            ))}

            <Button
                variant="outlined"
                color="secondary"
                startIcon={<AddIcon />}
                disabled={disabled}
                onClick={() => onChange([...fields, createEmptySchemaField(fields.length)])}
                sx={{ alignSelf: 'flex-start', borderWidth: 2, '&:hover': { borderWidth: 2 } }}
            >
                Add Field
            </Button>
        </Stack>
    );
};

export default MetadataSchemaBuilder;
